import { useMemo, useState } from "react";
import { MessageCircle, CreditCard, Loader2, Flame } from "lucide-react";
import { toast } from "sonner";
import { api, fmtBRL, waService } from "../../lib/api";
import { Reveal } from "../../lib/motion";

export default function Services({ services }) {
  const [category, setCategory] = useState("Todos");
  const [paying, setPaying] = useState(null);

  const categories = useMemo(() => {
    const set = new Set(services.map((s) => s.category).filter(Boolean));
    return ["Todos", ...Array.from(set)];
  }, [services]);

  const filtered =
    category === "Todos" ? services : services.filter((s) => s.category === category);

  const pay = async (service) => {
    if (paying) return;
    setPaying(service._id);
    try {
      const { data } = await api.post("/api/payments/checkout", {
        service_id: service._id,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Não foi possível iniciar o pagamento."
      );
      setPaying(null);
    }
  };

  return (
    <section id="servicos" data-testid="services-section" className="relative bg-paper py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.28em] text-crimson font-semibold flex items-center gap-3">
            <span className="w-8 h-px bg-crimson" /> Catálogo de serviços
          </p>
          <div className="mt-5 flex flex-wrap items-end justify-between gap-6">
            <h2 className="font-display font-extrabold tracking-tightest text-4xl md:text-6xl leading-[0.98] text-ink max-w-2xl">
              Escolha o serviço.
              <br />
              <span className="text-crimson">Contrate em minutos.</span>
            </h2>
            <p className="max-w-sm text-sm text-ink/55 leading-relaxed">
              Valores sempre atualizados. Contrate pelo WhatsApp ou pague online
              com cartão de crédito.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.1} className="mt-10 flex flex-wrap gap-2">
          {categories.map((c) => (
            <button
              key={c}
              data-testid={`service-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => setCategory(c)}
              className={`text-sm font-medium px-5 py-2.5 rounded-full border transition-all duration-300 ${
                category === c
                  ? "bg-ink text-paper border-ink"
                  : "border-line text-ink/60 hover:border-ink/40"
              }`}
            >
              {c}
            </button>
          ))}
        </Reveal>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((service, i) => (
            <Reveal
              key={service._id}
              delay={Math.min(i * 0.06, 0.3)}
              className={`h-full flex flex-col p-7 border transition-all duration-500 hover:-translate-y-1.5 ${
                service.highlight
                  ? "bg-ink text-paper border-ink"
                  : "bg-[#FFFDF8] text-ink border-line hover:border-ink/30"
              }`}
              testid={`service-card-${service._id}`}
            >
              <div className="flex items-center justify-between gap-2">
                <span
                  className={`text-[10px] uppercase tracking-[0.2em] font-semibold px-3 py-1 rounded-full ${
                    service.highlight
                      ? "bg-crimson text-paper"
                      : "bg-paper-2 text-ink/60"
                  }`}
                >
                  {service.category}
                </span>
                {service.highlight && (
                  <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-crimson-bright">
                    <Flame size={13} /> Mais procurado
                  </span>
                )}
              </div>

              <h3 className="mt-5 font-display font-bold text-2xl leading-snug">
                {service.name}
              </h3>
              <p
                className={`mt-3 text-sm leading-relaxed flex-1 ${
                  service.highlight ? "text-paper/65" : "text-ink/55"
                }`}
              >
                {service.description}
              </p>

              <div className="mt-6 pt-5 border-t border-current/10">
                {service.price ? (
                  <p className="font-display font-extrabold text-3xl tracking-tight">
                    {fmtBRL(service.price)}
                    <span className="text-sm font-medium opacity-50"> à vista</span>
                  </p>
                ) : (
                  <p className="font-display font-bold text-2xl">{service.price_label || "Sob consulta"}</p>
                )}

                <div className="mt-4 flex flex-col gap-2">
                  <a
                    data-testid={`service-whatsapp-${service._id}`}
                    href={waService(service.name)}
                    target="_blank"
                    rel="noreferrer"
                    className={`btn-sweep inline-flex items-center justify-center gap-2 font-semibold px-5 py-3.5 rounded-full transition-transform duration-300 hover:scale-[1.02] ${
                      service.highlight
                        ? "bg-crimson text-paper"
                        : "bg-crimson text-paper"
                    }`}
                  >
                    <MessageCircle size={17} />
                    Contratar via WhatsApp
                  </a>
                  {service.price ? (
                    <button
                      data-testid={`service-pay-${service._id}`}
                      onClick={() => pay(service)}
                      disabled={paying === service._id}
                      className={`inline-flex items-center justify-center gap-2 font-semibold px-5 py-3.5 rounded-full border transition-colors duration-300 ${
                        service.highlight
                          ? "border-line-dark text-paper hover:bg-paper/10"
                          : "border-ink/25 text-ink hover:bg-ink hover:text-paper"
                      }`}
                    >
                      {paying === service._id ? (
                        <Loader2 size={17} className="animate-spin" />
                      ) : (
                        <CreditCard size={17} />
                      )}
                      Pagar online
                    </button>
                  ) : (
                    <span
                      className={`text-center text-xs py-2 ${
                        service.highlight ? "text-paper/40" : "text-ink/40"
                      }`}
                    >
                      Orçamento personalizado pelo WhatsApp
                    </span>
                  )}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
